"use client";

import { Button, Heading, Image, Text } from "@pamperedchef/design-system";

import type { CallToAction } from "@/composites/foundation";

import * as styles from "./CollectionCards.module.scss";

export interface CollectionCard {
	title: string;
	description?: string;
	image?: { src: string; alt: string };
	cta?: CallToAction;
}

export interface CollectionCardsProps {
	heading?: string;
	cards: CollectionCard[];
	variant?: "cards" | "story";
	tinted?: boolean;
	action?: CallToAction;
	/** Navigation stays with the caller; composites do not touch `window`. */
	onCtaPress?(cta: CallToAction): void;
	onActionPress?(action: CallToAction): void;
}

function cx(...classes: Array<string | false | undefined>): string {
	return classes.filter(Boolean).join(" ");
}

/**
 * A row of editorial cards — photo, title, a line of copy and a button — as the
 * "Popular Collections" and "Why You'll Love" sections show them. Each card's
 * photo is also a plain link, so the destination is reachable without script.
 */
export function CollectionCards({
	heading,
	cards,
	variant = "cards",
	tinted = false,
	action,
	onCtaPress,
	onActionPress
}: CollectionCardsProps): JSX.Element | null {
	if (cards.length === 0) return null;

	return (
		<section
			className={cx(
				styles["cards"],
				variant === "story" && styles["cards--story"],
				tinted && styles["cards--tinted"]
			)}
			data-component="CollectionCards"
			data-variant={variant}
		>
			{heading || action ? (
				<div className={styles["cards__header"]}>
					{heading ? (
						<Heading tag="h2" className={styles["cards__heading"]}>
							{heading}
						</Heading>
					) : null}
					{action ? (
						<Button
							kind="secondary"
							size="sm"
							aria-label={action.assistiveText}
							onPress={() => onActionPress?.(action)}
						>
							{action.label}
						</Button>
					) : null}
				</div>
			) : null}

			<ul className={styles["cards__grid"]}>
				{cards.map((card, index) => (
					<li
						key={`${card.title}-${index}`}
						className={styles["card"]}
					>
						{card.image ? (
							card.cta ? (
								<a
									href={card.cta.href}
									aria-hidden="true"
									tabIndex={-1}
									className={styles["card__media"]}
								>
									<Image
										src={card.image.src}
										alt={card.image.alt}
										className={styles["card__image"]}
									/>
								</a>
							) : (
								<div className={styles["card__media"]}>
									<Image
										src={card.image.src}
										alt={card.image.alt}
										className={styles["card__image"]}
									/>
								</div>
							)
						) : null}

						<div className={styles["card__body"]}>
							<Heading tag="h3" className={styles["card__title"]}>
								{card.title}
							</Heading>
							{card.description ? (
								<Text
									tag="p"
									className={styles["card__description"]}
								>
									{card.description}
								</Text>
							) : null}
						</div>

						{card.cta ? (
							<Button
								kind="secondary"
								size="sm"
								className={styles["card__cta"]}
								aria-label={card.cta.assistiveText}
								onPress={() => {
									if (card.cta) onCtaPress?.(card.cta);
								}}
							>
								{card.cta.label}
							</Button>
						) : null}
					</li>
				))}
			</ul>
		</section>
	);
}
